import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Info, ShieldCheck } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";
import { apiError } from "../../shared/api/client";
import {
  platformApi,
  platformQueryKeys,
  type PlatformTenant,
  type TenantScreens,
} from "../../shared/api/platform";
import {
  planCatalogApi,
  planCatalogQueryKeys,
  type PlanCatalogRow,
} from "../../shared/api/platformBilling";

/**
 * Gói của một phòng khám và việc áp dụng giới hạn theo gói (ADR-0053).
 *
 * <p><b>Gán gói</b> dành cho phòng khám ký hợp đồng ngoài hệ thống hoặc được chuyển gói theo thoả
 * thuận: không qua ví, gói nào cũng gán được kể cả gói chưa mở bán. Hiếm khi làm, nên form gập sau
 * nút "Đổi gói" và mang ô lý do của riêng nó — lý do nằm cạnh đúng thao tác nó giải thích.
 *
 * <p><b>Áp dụng giới hạn</b> (máy chủ gọi là "chặn theo gói", `enforced`) bật cho từng phòng khám.
 * Chưa áp dụng thì phòng khám vẫn thấy toàn bộ menu, và danh sách tính năng bên dưới mới chỉ là
 * điều sẽ xảy ra — nên trình tự đúng là gán gói, rà danh sách, rồi mới áp dụng. Gỡ là nút lùi:
 * phòng khám thấy lại toàn bộ menu ngay. Chưa có gói thì máy chủ từ chối (409), nên nút khoá sẵn.
 */
export function PlatformTenantPlanSection({
  tenant,
  data,
}: {
  tenant: PlatformTenant;
  data: TenantScreens;
}) {
  const qc = useQueryClient();
  const [assigning, setAssigning] = useState(false);

  const plans = useQuery({
    queryKey: planCatalogQueryKeys.all(),
    queryFn: () => planCatalogApi.list(),
  });

  const refresh = () => {
    void qc.invalidateQueries({
      queryKey: platformQueryKeys.tenantScreens(tenant.id),
    });
    void qc.invalidateQueries({ queryKey: planCatalogQueryKeys.all() });
  };

  const enforce = useMutation({
    mutationFn: (enabled: boolean) =>
      platformApi.setPlanEnforcement(tenant.id, enabled),
    onSuccess: refresh,
  });

  const hasPlan = !!data.planCode;
  const planName = plans.data?.find((p) => p.code === data.planCode)?.name;
  const enforceBlocked = !data.enforced && !hasPlan;

  return (
    <section
      className="rounded-lg border"
      aria-label="Gói và giới hạn theo gói"
    >
      <div className="grid divide-y sm:grid-cols-2 sm:divide-x sm:divide-y-0">
        <div className="flex items-center gap-3 px-4 py-3">
          <div className="min-w-0 flex-1">
            <p className="text-xs text-muted-foreground">Gói đang dùng</p>
            <p className="truncate font-semibold">
              {hasPlan ? (planName ?? data.planCode) : "Chưa có gói"}
            </p>
          </div>
          {!assigning && (
            <Button
              size="sm"
              variant="outline"
              onClick={() => setAssigning(true)}
            >
              {hasPlan ? "Đổi gói" : "Gán gói"}
            </Button>
          )}
        </div>

        <div className="flex items-center gap-3 px-4 py-3">
          <div className="min-w-0 flex-1">
            <p className="text-xs text-muted-foreground">
              Giới hạn menu theo gói
            </p>
            <p
              className={cn(
                "flex items-center gap-1.5 font-semibold",
                data.enforced ? "text-emerald-600" : "text-amber-600",
              )}
            >
              {data.enforced ? (
                <ShieldCheck className="h-4 w-4" />
              ) : (
                <Info className="h-4 w-4" />
              )}
              {data.enforced ? "Đang áp dụng" : "Chưa áp dụng"}
            </p>
          </div>
          <Button
            size="sm"
            variant={data.enforced ? "outline" : "default"}
            disabled={enforce.isPending || enforceBlocked}
            title={enforceBlocked ? "Gán gói trước" : undefined}
            onClick={() => enforce.mutate(!data.enforced)}
          >
            {data.enforced ? "Gỡ giới hạn" : "Áp dụng"}
          </Button>
        </div>
      </div>

      <p className="border-t px-4 py-2 text-xs text-muted-foreground">
        {data.enforced
          ? "Phòng khám chỉ thấy những tính năng đang bật bên dưới."
          : hasPlan
            ? "Phòng khám vẫn thấy mọi tính năng. Rà danh sách bên dưới rồi bấm Áp dụng."
            : "Phòng khám vẫn thấy mọi tính năng. Gán gói trước rồi mới áp dụng được."}
      </p>
      {enforce.error && (
        <p role="alert" className="border-t px-4 py-2 text-sm text-destructive">
          {apiError(enforce.error).detail}
        </p>
      )}

      {assigning && (
        <div className="border-t px-4 pb-4">
          <AssignPlanForm
            tenant={tenant}
            plans={plans.data ?? []}
            initialPlanCode={data.planCode ?? ""}
            onDone={() => {
              setAssigning(false);
              refresh();
            }}
            onCancel={() => setAssigning(false)}
          />
        </div>
      )}
    </section>
  );
}

function AssignPlanForm({
  tenant,
  plans,
  initialPlanCode,
  onDone,
  onCancel,
}: {
  tenant: PlatformTenant;
  plans: PlanCatalogRow[];
  initialPlanCode: string;
  onDone: () => void;
  onCancel: () => void;
}) {
  const [planCode, setPlanCode] = useState(initialPlanCode);
  const [activeTo, setActiveTo] = useState("");
  const [reason, setReason] = useState("");
  // Lỗi hiện SAU khi bấm, không khoá nút: nút khoá thì người dùng không biết còn thiếu gì.
  const [errors, setErrors] = useState<{ planCode?: string; reason?: string }>(
    {},
  );

  const assign = useMutation({
    mutationFn: () =>
      platformApi.assignTenantPlan(tenant.id, {
        planCode,
        // Ô ngày cho một ngày; gói hết hạn vào CUỐI ngày đó theo giờ Việt Nam.
        activeTo: activeTo ? `${activeTo}T23:59:59+07:00` : null,
        reason: reason.trim(),
      }),
    onSuccess: onDone,
  });

  const validate = () => {
    const next: typeof errors = {};
    if (planCode === "") next.planCode = "Vui lòng chọn gói.";
    if (reason.trim() === "") next.reason = "Vui lòng nhập lý do.";
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  return (
    <form
      className="mt-4 grid gap-4 sm:grid-cols-2"
      noValidate
      onSubmit={(e) => {
        e.preventDefault();
        if (assign.isPending) return;
        if (validate()) assign.mutate();
      }}
    >
      <label className="grid gap-1.5 text-sm">
        <span className="font-medium">Gói mới</span>
        <select
          className={cn(
            "h-9 rounded-md border bg-background px-2",
            errors.planCode && "border-destructive",
          )}
          value={planCode}
          aria-invalid={errors.planCode ? true : undefined}
          onChange={(e) => {
            setPlanCode(e.target.value);
            setErrors((prev) => ({ ...prev, planCode: undefined }));
          }}
        >
          <option value="">— chọn gói —</option>
          {plans.map((p) => (
            <option key={p.id} value={p.code}>
              {p.name} ({p.code})
            </option>
          ))}
        </select>
        {errors.planCode && (
          <span role="alert" className="text-xs text-destructive">
            {errors.planCode}
          </span>
        )}
      </label>
      <label className="grid gap-1.5 text-sm">
        <span className="font-medium">
          Hết hạn{" "}
          <span className="font-normal text-muted-foreground">
            (không bắt buộc)
          </span>
        </span>
        <Input
          type="date"
          value={activeTo}
          onChange={(e) => setActiveTo(e.target.value)}
        />
      </label>
      <label className="grid gap-1.5 text-sm sm:col-span-2">
        <span className="font-medium">
          Lý do <span className="text-destructive">*</span>
        </span>
        <Input
          value={reason}
          maxLength={500}
          aria-required
          aria-invalid={errors.reason ? true : undefined}
          className={cn(errors.reason && "border-destructive")}
          onChange={(e) => {
            setReason(e.target.value);
            setErrors((prev) => ({ ...prev, reason: undefined }));
          }}
          placeholder="Ví dụ: ký hợp đồng gói Cơ bản ngày 01/10"
        />
        {errors.reason ? (
          <span role="alert" className="text-xs text-destructive">
            {errors.reason}
          </span>
        ) : (
          <span className="text-xs text-muted-foreground">
            Ghi vào nhật ký của phòng khám.
          </span>
        )}
      </label>
      {assign.error && (
        <p role="alert" className="text-sm text-destructive sm:col-span-2">
          {apiError(assign.error).detail}
        </p>
      )}
      <div className="flex justify-end gap-2 sm:col-span-2">
        <Button type="button" size="sm" variant="ghost" onClick={onCancel}>
          Huỷ
        </Button>
        <Button type="submit" size="sm" disabled={assign.isPending}>
          {assign.isPending ? "Đang gán…" : "Gán gói"}
        </Button>
      </div>
    </form>
  );
}
